import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { UserCircle, Phone, Lock } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HandlerLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [appName, setAppName] = useState('DWS Vault Chat');
  const [needsDeviceName, setNeedsDeviceName] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [pendingApproval, setPendingApproval] = useState(false);

  const { login, user } = useAuth();

  const getDeviceId = () => {
    let deviceId = localStorage.getItem('handler_device_id');
    if (!deviceId) {
      deviceId = 'dev_' + (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substr(2) + Date.now());
      localStorage.setItem('handler_device_id', deviceId);
    }
    return deviceId;
  };

  // Fetch app name
  useEffect(() => {
    const fetchAppName = async () => {
      try {
        const response = await axios.get(`${API}/settings/support-info`);
        if (response.data.app_name) {
          setAppName(response.data.app_name);
          document.title = `${response.data.app_name} - Handler Login`;
        }
      } catch (error) {
        console.error('Failed to fetch app name:', error);
      }
    };
    fetchAppName();
  }, []);

  const navigate = useNavigate();

  // Auto-redirect only if already logged in AS HANDLER (matching role)
  // If user is logged in as different role, allow them to see this page and log in as handler
  useEffect(() => {
    if (user && user.role === 'calling_handler') {
      navigate('/calling-handler-dashboard', { replace: true });
    } else if (user && ['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler'].includes(user.role)) {
      navigate('/handler-dashboard', { replace: true });
    }
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        phone: username,  // Send username in phone field for backend compatibility
        password,
        device_id: getDeviceId(),
        device_name: needsDeviceName ? deviceName.trim() : undefined,
      });

      const userData = response.data;

      // New-device-restriction gate (points 10/45): admin toggle requires an
      // unrecognized device to submit a name and wait for approval.
      if (userData.requires_device_approval) {
        if (userData.needs_name) {
          setNeedsDeviceName(true);
          setError('');
        } else if (userData.pending) {
          setPendingApproval(true);
          setError('');
        }
        setLoading(false);
        return;
      }

      // STRICT ROLE CHECK: Only allow handlers on this page
      if (!['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'third_party_withdraw_handler', 'calling_handler'].includes(userData.role)) {
        setError('This is Handler login page. Please use the correct login page for your role.');
        setLoading(false);
        return;
      }

      login(userData);
      
      // Track activity for login (fire-and-forget — must not block navigation)
      axios.post(`${API}/admin/activity/track`, {
        user_id: userData.id,
        action: 'login'
      }).catch((trackErr) => console.error('Activity tracking failed:', trackErr));
      
      // Point 48 — Calling Handler gets its own stripped-down dashboard.
      navigate(userData.role === 'calling_handler' ? '/calling-handler-dashboard' : '/handler-dashboard');
    } catch (err) {
      if (err.response?.status === 429) {
        setError('Too many attempts from this device — please wait a minute and try again.');
      } else {
        setError(err.response?.data?.detail || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4" style={{ position: 'fixed', inset: 0, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md my-8">
        <div className="text-center mb-8">
          <div className="w-28 h-28 mx-auto mb-4 flex items-center justify-center">
            <img 
              src="/icons/customer/icon-128x128.png" 
              alt="DWS Vault Chat"
              className="w-28 h-28 object-contain"
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-800">{appName}</h1>
          <p className="text-gray-600 mt-2">Handler Login</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Username or Number
            </label>
            <div className="relative">
              <Phone size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value.slice(0, 10))}
                maxLength={10}
                placeholder="Enter username or number"
                required
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="handler-username-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <Lock size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {needsDeviceName && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New device detected — name it for admin approval
              </label>
              <input
                type="text"
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                placeholder="e.g. My Phone, Office Laptop"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="handler-device-name-input"
              />
            </div>
          )}

          {pendingApproval && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg text-sm">
              Request sent to admin for this device. You'll be able to log in once approved — try again shortly.
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Logging in...' : needsDeviceName ? 'Request Access' : pendingApproval ? 'Try Again' : 'Login'}
          </button>

          <div className="text-center mt-3">
            <button
              type="button"
              onClick={async () => {
                if (!username || username.length < 3) {
                  alert('Please enter your registered username or phone first');
                  return;
                }
                try {
                  await axios.post(`${API}/auth/forgot-password`, { phone: username });
                  alert('✅ Your password reset request has been sent to Admin.\nAdmin will contact you with your new password.');
                } catch (e) {
                  alert('Failed to send reset request. Please try again.');
                }
              }}
              className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium"
              data-testid="handler-forgot-password-btn"
            >
              Forgot Password? Send request to Admin
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HandlerLogin;